import { call, fork, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { notification } from 'ui';
import callApi from 'utils/api';
import moment from 'utils/cops-moment';
import { planningBoardWorklistId, sortTypes, urlSigns } from '../../../utils/app-configs';
import { generatePreviewId } from '../../../utils/helpers';
import { getWorklistDataByIdRequest } from '../../../worklist/containers/worklist-actions';
import { resetMultiPolling } from '../../publish-steps/containers/publish-steps-actions';
import { PUBLISH_TO_SAP_QUICK_ACTION } from '../../toolbar/containers/constants';
import {
  publishToSapQuickActionFailure,
  publishToSapQuickActionSuccess,
} from '../../toolbar/containers/toolbar-dynamic-actions-actions';
import { pageSize, viewIds } from '../config/view';
import { getDateToString } from '../utils/date-functions';
import {
  abortRouteOptimisationFailure,
  abortRouteOptimisationSuccess,
  addBlockDataFailure,
  addBlockDataSuccess,
  addMultiBlockDataFailure,
  addMultiBlockDataSuccess,
  calculateRouteOptimisationFailure,
  calculateRouteOptimisationSuccess,
  createGanttViewFailure,
  createGanttViewSuccess,
  deletePlanningFailure,
  deletePlanningSuccess,
  fetchBlockData,
  fetchBlockDataFailure,
  fetchBlockDataSuccess,
  fetchBoardDataFailure,
  fetchBoardDataSuccess,
  fetchBoardMetaDataFailure,
  fetchBoardMetaDataSuccess,
  fetchForwardBlockData,
  fetchForwardBlockDataFailure,
  fetchForwardBlockDataSuccess,
  fetchPlanningDataPostSocketUpdateFailure,
  fetchPlanningDataPostSocketUpdateSuccess,
  fetchPublishAllCount,
  fetchPublishAllCountFailure,
  fetchPublishAllCountSuccess,
  fetchRecommendedEngineersFailure,
  fetchRecommendedEngineersSuccess,
  fetchViewDataFailure,
  fetchViewDataSuccess,
  getGanttViewsFailure,
  getGanttViewsSuccess,
  markDateConfirmedFailure,
  markDateConfirmedSuccess,
  markDateUnconfirmedFailure,
  markDateUnconfirmedSuccess,
  openPlanningAndView as openPlanningAndViewAction,
  openPlanningAndViewFailure,
  openPlanningAndViewSuccess,
  publishAllToSAPFailure,
  publishAllToSAPSuccess,
  publishToSAPFailure,
  publishToSAPSuccess,
  recalculateTravelTimeFailure,
  recalculateTravelTimeSuccess,
  restoreBackupFailure,
  restoreBackupSuccess,
  retryRouteOptimisationFailure,
  retryRouteOptimisationSuccess,
  setAdjacentBlock,
  setIsDraggingOver,
  setViewInternal,
  splitBlockFailure,
  splitBlockSuccess,
  updateBlockDataFailure,
  updateBlockDataSuccess,
  updateBlockOtherLaneDataSuccess,
  updateDataFromSockets,
  updateGanttViewsFailure,
  updateGanttViewsSuccess,
  updateMultiPlanningBlockDataFailure,
  updateMultiPlanningBlockDataSuccess,
} from './gantt-actions';
import * as actionTypes from './gantt-constants';

export const getDateReference = state => state.gantt.dateReference;
export const getLanesInfo = state => state.gantt.lanesInfo;
export const getPlanningIds = state => state.gantt.planningsToPublish;
export const getBoardData = state => state.gantt.boardData;
export const getAllFSEs = state => state.gantt.allFSEsList;
export const getDefaultSort = state => state.worklist.metaData.defaultSorting;
export const getAdjacentBlock = state => state.gantt.adjacentBlock;
export const getShowTravelTime = state => state.planningBoard.showTravelTime;
export const getConnectionInfo = state => state.sockets;
const worklistId = 'serviceplanning';

function* checkAdjacentBlock(body) {
  const adjacentBlock = yield select(getAdjacentBlock);
  if (!adjacentBlock) {
    return body;
  }
  let labourEnd = adjacentBlock.subBlocks?.find(sb => sb.activityType === 'travelFrom')?.start;
  if (!labourEnd) {
    const labour = adjacentBlock.subBlocks?.find(sb => sb.activityType === 'labour');
    if (labour) {
      const durationInMinutes = moment.duration(labour.duration).asMinutes();
      labourEnd = moment(labour.start).clone().add(durationInMinutes, 'minutes');
    } else if (
      adjacentBlock.type !== 'fieldservice') {
      const durationInMinutes = moment.duration(adjacentBlock.duration).asMinutes();
      labourEnd = moment(adjacentBlock.start).clone().add(durationInMinutes, 'minutes');
    }
  }
  const newBody = {
    ...body,
    adjacentBlockId: adjacentBlock?.id,
    start: labourEnd || body.start,
  };
  yield put(setAdjacentBlock(null));
  return newBody;
}

function checkIfConnected({ connection, subscriptions }) {
  return subscriptions?.find(id => id.includes(connection?.connection?.connectionId));
}

export function* doFetchBoardMetadate(action) {
  try {
    const results = yield call(callApi, {
      endpoint: 'planning/serviceplanning/metadata',
      options: {
        method: 'GET',
        errorMessage: 'Could not get board meta data',
        retryAction: action,
      },
    });
    yield put(fetchBoardMetaDataSuccess(results));
  } catch (err) {
    yield put(fetchBoardMetaDataFailure(err));
  }
}

export function* doFetchBoardData(action) {
  try {
    const { sortedFSEs, sort, isAutoSorted } = action.payload;
    const body = isAutoSorted ? [] : sortedFSEs;
    const results = yield call(callApi, {
      endpoint:
        // eslint-disable-next-line
        `planning/${action.payload.boardId}?page=${action.payload.page}&pageSize=${pageSize}&sort=${sort}${action.payload.filters ? `&filters=${action.payload.filters}` : ''
        }`,
      options: {
        method: 'POST',
        body: JSON.stringify(body),
        errorMessage: 'Could not get board data',
        retryAction: action,
      },
    });
    yield put(fetchBoardDataSuccess(results));
  } catch (err) {
    yield put(fetchBoardDataFailure(err));
  }
}

export function* doFetchViewData(action) {
  try {
    const refetchSubSet = !!action?.payload?.refetchSubSet;
    const lanes = action.payload.lanes?.map(l => `lanes=${l}`).join('&');
    const results = yield call(callApi, {
      endpoint:
        // eslint-disable-next-line
        `planning/${action.payload.boardId}/${action.payload.viewId}/${action.payload.viewDate}${lanes.length ? `?${lanes}` : ''
        }`,
      options: {
        errorMessage: 'Could not get view data',
        retryAction: action,
      },
    });
    yield put(fetchPublishAllCount({ boardId: action.payload.boardId }));
    yield put(fetchViewDataSuccess({ ...results, refetchSubSet }));
  } catch (err) {
    yield put(fetchViewDataFailure(err));
  }
}

export function* doUpdateBlockData(action) {
  const boardData = yield select(getBoardData);
  const showTravelTime = yield select(getShowTravelTime);
  const connection = yield select(getConnectionInfo);
  const isConnected = checkIfConnected(connection);

  const { calculateTravelTime } = boardData;

  try {
    const { type, oldLaneId, successCb, isAbsenceOrReservation } = action.payload;
    let body = {
      laneId: action.payload.laneId,
      start: action.payload.start,
      durations: action.payload.durations,
      description: action.payload.description,
      startFromMainActivity: !calculateTravelTime && !showTravelTime,
    };

    if (!isAbsenceOrReservation) {
      body = yield checkAdjacentBlock(body);
    } else {
      yield put(setAdjacentBlock(null));
    }

    Object.keys(body).forEach(key => (body[key] == null) && delete body[key]);

    const results = yield call(callApi, {
      endpoint:
        // eslint-disable-next-line
        `planning/${action.payload.boardId}/${action.payload.planningId}`,
      options: {
        method: 'POST',
        body: JSON.stringify(body),
        errorMessage: 'Could not update block data',
        retryAction: action,
      },
    });
    if (successCb) {
      successCb();
    }

    if (!isConnected) {
      yield put(fetchPublishAllCount({ boardId: action.payload.boardId }));
      if (results?.block?.objectId) {
        yield put(getWorklistDataByIdRequest({
          ids: [results?.block?.objectId, ...results?.additionalBlocks?.map(a => a.objectId)],
          worklistId,
          highlightIds: [
            results?.block?.objectId,
            action.payload.planningId,
            ...results?.additionalBlocks?.map(a => a.objectId),
            ...results?.additionalBlocks?.map(a => a.id),
          ],
        }));
      }
      if (type === 'updateSameLane') {
        yield put(updateBlockDataSuccess({
          ...results,
          isSidePanel: action.payload.isSidePanel,
        }));
      } else {
        yield put(updateBlockOtherLaneDataSuccess({
          ...results,
          oldLaneId,
          isSidePanel: action.payload.isSidePanel,
        }));
      }
    } else {
      yield put(updateDataFromSockets())
    }
  } catch (err) {
    yield put(updateBlockDataFailure({
      ...err,
      planningId: action.payload.planningId,
      laneId: action.payload.laneId,
      oldLaneId: action.payload.oldLaneId,
      linkedEntityId: action.payload.linkedEntityId,
      isSidePanel: action.payload.isSidePanel,
    }));
  }
}

export function* doFetchForwardBlockData(action) {
  try {
    const results = yield call(callApi, {
      endpoint: 'planning/forward/blocks',
      options: {
        method: 'POST',
        body: JSON.stringify({
          byForwardPlanningIds: action.payload.id ? [action.payload.id] : null,
        }),
        errorMessage: 'Could not fetch block data',
        retryAction: action,
      },
    });

    yield put(fetchForwardBlockDataSuccess(results));
  } catch (err) {
    yield put(fetchForwardBlockDataFailure(err));
  }
}

export function* doFetchBlockData(action) {
  try {
    const results = yield call(callApi, {
      endpoint:
        `planning/${action.payload.boardId}/${action.payload.id}`,
      options: {
        errorMessage: 'Could not fetch block data',
        retryAction: action,
      },
    });
    if (results?.objectId) {
      yield put(getWorklistDataByIdRequest({
        ids: [results?.objectId],
        worklistId,
        highlightIds: [results?.objectId, action.payload.id],
      }));
    }
    yield put(fetchBlockDataSuccess({
      ...results,
      isSidePanel: action.payload.isSidePanel,
    }));
  } catch (err) {
    yield put(fetchBlockDataFailure({
      ...err,
      isSidePanel: action.payload.isSidePanel,
    }));
  }
}

export function* doPublishToSAP(action) {
  const isTimeline = !!action.payload.isTimeline;
  const publishWithQuickAction = !!action.payload.publishWithQuickAction;
  try {
    const planningIds = action.payload.planningIds || [];

    const results = yield call(callApi, {
      endpoint:
        `planning/${action.payload.boardId}/publish`,
      options: {
        method: 'POST',
        body: JSON.stringify(planningIds),
        errorMessage: 'Could not publish to SAP',
        retryAction: action,
      },
    });
    // if it is timeline, we don't need to fetch worklist data and others
    if (!isTimeline) {
      if (results?.succeeded?.filter(r => r.objectId)?.length) {
        yield put(getWorklistDataByIdRequest({
          ids: results?.succeeded?.filter(r => r.objectId)?.map(r => r.objectId),
          worklistId,
          highlightIds: [
            ...results?.succeeded?.filter(r => r.objectId)?.map(r => r.objectId),
            ...planningIds,
          ],
        }));
      }
      yield put(fetchPublishAllCount({ boardId: action.payload.boardId }));
      yield put(resetMultiPolling({ shouldReset: true, pollingInterval: results.pollingInterval }));
    }

    // if you publish with quick action, you use a different action
    if (publishWithQuickAction) {
      yield put(publishToSapQuickActionSuccess(results));
    } else {
      yield put(publishToSAPSuccess({
        ...results,
        isSidePanel: action.payload.isSidePanel,
      }));
    }
  } catch (err) {
    if (publishWithQuickAction) {
      yield put(publishToSapQuickActionFailure(err));
    } else {
      yield put(publishToSAPFailure(err));
    }
  }
}

export function* doPublishAllToSAP(action) {
  try {
    const planningIds = yield select(getPlanningIds);

    const results = yield call(callApi, {
      endpoint:
        `planning/${action.payload.boardId}/publish`,
      options: {
        method: 'POST',
        body: JSON.stringify(planningIds),
        errorMessage: 'Could not publish to SAP',
        retryAction: action,
      },
    });
    if (results?.succeeded?.filter(r => r.objectId)?.length) {
      yield put(getWorklistDataByIdRequest({
        ids: results?.succeeded?.filter(r => r.objectId)?.map(r => r.objectId),
        worklistId,
        highlightIds: [
          ...results?.succeeded?.filter(r => r.objectId)?.map(r => r.objectId),
          ...planningIds,
        ],
      }));
    }
    yield put(fetchPublishAllCount({ boardId: action.payload.boardId }));
    yield put(resetMultiPolling({ shouldReset: true, pollingInterval: results?.pollingInterval }));
    yield put(publishAllToSAPSuccess(results));
  } catch (err) {
    yield put(publishAllToSAPFailure(err));
  }
}

export function* doMarkDateConfirmed(action) {
  const {
    boardId,
    planningId,
    isSidePanel,
  } = action.payload;
  const connection = yield select(getConnectionInfo);
  const isConnected = checkIfConnected(connection);
  try {
    const results = yield call(callApi, {
      endpoint: `planning/${boardId}/${planningId}/markDateConfirmed`,
      options: {
        method: 'POST',
        errorMessage: 'Could not mark date confirmed',
        retryAction: action,
      },
    });
    if (!isConnected) {
      yield put(fetchPublishAllCount({ boardId }));
      if (results?.objectId) {
        yield put(getWorklistDataByIdRequest({
          ids: [results?.objectId],
          worklistId,
          highlightIds: [results?.objectId, planningId],
        }));
      }
      yield put(resetMultiPolling({
        shouldReset: true,
        pollingInterval: results?.operationInfo?.pollingInterval || 10,
      }));
      yield put(markDateConfirmedSuccess({
        ...results,
        isSidePanel,
      }));
    }
  } catch (err) {
    yield put(markDateConfirmedFailure(err));
  }
}

export function* doMarkDateUnconfirmed(action) {
  const connection = yield select(getConnectionInfo);
  const isConnected = checkIfConnected(connection);
  try {
    const results = yield call(callApi, {
      endpoint:
        `planning/${action.payload.boardId}/${action.payload.planningId}/markDateUnconfirmed`,
      options: {
        method: 'POST',
        errorMessage: 'Could not mark date unconfirmed',
        retryAction: action,
      },
    });
    if (action.payload.successCb) {
      action.payload.successCb();
    }
    if (!isConnected) {
      yield put(fetchPublishAllCount({ boardId: action.payload.boardId }));
      if (results?.objectId) {
        yield put(getWorklistDataByIdRequest({
          ids: [results?.objectId],
          worklistId,
          highlightIds: [results?.objectId, action.payload.planningId],
        }));
      }
      yield put(markDateUnconfirmedSuccess({
        ...results,
        isSidePanel: action.payload.isSidePanel,
      }));
    } else {
      yield put(updateDataFromSockets())
    }
  } catch (err) {
    yield put(markDateUnconfirmedFailure(err));
  }
}

export function* doRestoreBackup(action) {
  const connection = yield select(getConnectionInfo);
  const isConnected = checkIfConnected(connection);
  try {
    const results = yield call(callApi, {
      endpoint: `planning/${action.payload.boardId}/${action.payload.planningId}/undo`,
      options: {
        method: 'POST',
        errorMessage: 'Could not restore backup',
        retryAction: action,
      },
    });
    if (!isConnected) {
      yield put(fetchPublishAllCount({ boardId: action.payload.boardId }));
      if (results?.block?.objectId) {
        yield put(getWorklistDataByIdRequest({
          ids: [results?.block?.objectId],
          worklistId,
          highlightIds: [results?.block?.objectId, action.payload.planningId],
        }));
      }
      yield put(restoreBackupSuccess({
        ...results,
        isSidePanel: action.payload.isSidePanel,
      }));
    } else {
      yield put(updateDataFromSockets())
    }
  } catch (err) {
    yield put(restoreBackupFailure(err));
  }
}

export function* doRecalculateTravelTime(action) {
  try {
    const results = yield call(callApi, {
      endpoint: `planning/${action.payload.boardId}/${action.payload.planningId}/recalculate`,
      options: {
        method: 'POST',
        errorMessage: 'Could not recalculate travel time',
        retryAction: action,
      },
    });
    if (results?.block?.objectId) {
      yield put(getWorklistDataByIdRequest({
        ids: [results?.block?.objectId],
        worklistId,
        highlightIds: [results?.block?.objectId, action.payload.planningId],
      }));
    }
    yield put(fetchPublishAllCount({ boardId: action.payload.boardId }));
    yield put(recalculateTravelTimeSuccess({
      ...results,
      isSidePanel: action.payload.isSidePanel,
    }));
  } catch (err) {
    yield put(recalculateTravelTimeFailure(err));
  }
}

export function* doAddBlockData(action) {
  const boardData = yield select(getBoardData);
  const showTravelTime = yield select(getShowTravelTime);
  const { calculateTravelTime } = boardData;
  const connection = yield select(getConnectionInfo);
  const isConnected = checkIfConnected(connection);

  try {
    let body = {
      linkedEntityId: action.payload.linkedEntityId,
      linkedEntityType: action.payload.linkedEntityType,
      start: action.payload.start,
      duration: action.payload.duration,
      durationDisplayUnit: action.payload?.durationDisplayUnit ?? 'hours',
      type: action.payload.type,
      description: action.payload.description,
      startFromMainActivity: !calculateTravelTime && !showTravelTime,
    };

    body = yield checkAdjacentBlock(body);

    const results = yield call(callApi, {
      endpoint:
        // eslint-disable-next-line
        `planning/${action.payload.boardId}/${action.payload.laneId}`,
      options: {
        method: 'PUT',
        body: JSON.stringify(body),
        errorMessage: 'Could not add block data',
        retryAction: action,
      },
    });
    if (!isConnected) {
      yield put(fetchPublishAllCount({ boardId: action.payload.boardId }));
      if (results?.block?.objectId) {
        yield put(getWorklistDataByIdRequest({
          ids: [results?.block?.objectId, ...results?.additionalBlocks?.map(a => a.objectId)],
          worklistId,
          highlightIds: [
            results?.block?.objectId,
            results?.block?.id,
            ...results?.additionalBlocks?.map(a => a.objectId),
            ...results?.additionalBlocks?.map(a => a.id),
          ],
        }));
      }
      yield put(addBlockDataSuccess({
        ...results,
        isSidePanel: action.payload.isSidePanel,
      }));
    } else {
      yield put(updateDataFromSockets())
    }
  } catch (err) {
    yield put(addBlockDataFailure({
      ...err,
      id: generatePreviewId(action.payload),
      laneId: action.payload.laneId,
      isSidePanel: action.payload.isSidePanel,
    }));
  }
}

export function* doAddMultiBlockData(action) {
  const boardData = yield select(getBoardData);
  const showTravelTime = yield select(getShowTravelTime);
  const { calculateTravelTime } = boardData;

  try {
    let body = {
      entities: action.payload.entities,
      start: action.payload.start,
      startFromMainActivity: !calculateTravelTime && !showTravelTime,
    };

    body = yield checkAdjacentBlock(body);

    const results = yield call(callApi, {
      endpoint:
        // eslint-disable-next-line
        `planning/${action.payload.boardId}/${action.payload.laneId}/multi`,
      options: {
        method: 'POST',
        body: JSON.stringify(body),
        errorMessage: 'Could not add block data',
        retryAction: action,
      },
    });

    const orderIds = results.blocks?.filter(r => r.objectId)?.map(b => b.objectId);
    yield put(fetchPublishAllCount({ boardId: action.payload.boardId }));
    if (orderIds?.length) {
      yield put(getWorklistDataByIdRequest({
        ids: orderIds,
        worklistId,
        highlightIds: [...orderIds, ...results.blocks?.map(b => b.id)],
      }));
    }
    yield put(addMultiBlockDataSuccess({
      ...results,
      laneId: action.payload.laneId,
      blockIds: action.payload.entities?.map(e => e.linkedEntityId),
    }));
  } catch (err) {
    yield put(addMultiBlockDataFailure({
      ...err,
      id: generatePreviewId({
        laneId: action.payload.laneId,
        linkedEntityId: action.payload.entities?.map(e => e.linkedEntityId)
          .join(urlSigns.keyValueSeparator),
      }),
      laneId: action.payload.laneId,
    }));
  }
}

export function* doUpdateMultiPlanningBlockData(action) {
  const boardData = yield select(getBoardData);
  const showTravelTime = yield select(getShowTravelTime);
  const { calculateTravelTime } = boardData;

  try {
    let body = {
      entities: action.payload.entities,
      start: action.payload.start,
      startFromMainActivity: !calculateTravelTime && !showTravelTime,
    };

    body = yield checkAdjacentBlock(body);

    const results = yield call(callApi, {
      endpoint:
        // eslint-disable-next-line
        `planning/${action.payload.boardId}/${action.payload.laneId}/multi`,
      options: {
        method: 'POST',
        body: JSON.stringify(body),
        errorMessage: 'Could not update block data',
        retryAction: action,
      },
    });
    yield put(fetchPublishAllCount({ boardId: action.payload.boardId }));
    const orderIds = results.blocks?.filter(r => r.objectId)?.map(b => b.objectId);
    if (orderIds?.length) {
      yield put(getWorklistDataByIdRequest({
        ids: orderIds,
        worklistId,
        highlightIds: [...orderIds, ...results.blocks?.map(b => b.id)],
      }));
    }
    yield put(updateMultiPlanningBlockDataSuccess({
      ...results,
      laneId: action.payload.laneId,
      blockIds: action.payload.entities?.map(e => e.linkedEntityId),
      planningItemObject: action.payload.entities?.map(e => e.planningItemObject),
    }));

  } catch (err) {
    yield put(updateMultiPlanningBlockDataFailure({
      ...err,
      id: generatePreviewId({
        laneId: action.payload.laneId,
        linkedEntityId: action.payload.entities?.map(e => e.linkedEntityId)
          .join(urlSigns.keyValueSeparator),
      }),
      laneId: action.payload.laneId,
    }));
  }
}

export function* doDeleteBlock(action) {
  const connection = yield select(getConnectionInfo);
  const isConnected = checkIfConnected(connection);
  try {
    const results = yield call(callApi, {
      endpoint:
        // eslint-disable-next-line
        `planning/${action.payload.boardId}/${action.payload.planningId}`,
      options: {
        method: 'DELETE',
        errorMessage: 'Could not delete block data',
        retryAction: action,
      },
    });
    if (!isConnected) {
      yield put(fetchPublishAllCount({ boardId: action.payload.boardId }));
      if (results?.block?.objectId) {
        yield put(getWorklistDataByIdRequest({
          ids: [results?.block?.objectId, ...results?.additionalBlocks?.map(a => a.objectId)],
          worklistId,
          highlightIds: [
            results?.block?.objectId,
            ...results?.additionalBlocks?.map(a => a.objectId),
            ...results?.additionalBlocks?.map(a => a.id),
          ],
        }));
      } else {
        yield put(resetMultiPolling({ shouldReset: true }));
      }
      yield put(deletePlanningSuccess({
        ...results,
        isSidePanel: action.payload.isSidePanel,
      }));
    } else {
      yield put(updateDataFromSockets())
    }
  } catch (err) {
    yield put(deletePlanningFailure({
      ...err,
      isSidePanel: action.payload.isSidePanel,
    }));
  }
}

export function* doSplitBlock(action) {
  const connection = yield select(getConnectionInfo);
  const isConnected = checkIfConnected(connection);
  try {
    const results = yield call(callApi, {
      endpoint:
        // eslint-disable-next-line
        `planning/${action.payload.boardId}/${action.payload.planningId}/split?splitType=Divide5050`,
      options: {
        method: 'POST',
        errorMessage: 'Could not split block',
        retryAction: action,
      },
    });
    if (action.payload.refetch) {
      yield put(
        fetchBlockData({
          id: results.splittedBlock.id,
          boardId: action.payload.boardId,
          isSidePanel: true,
        }),
      );
    }
    if (action.payload.successCb) {
      action.payload.successCb();
    }
    if (!isConnected) {
      yield put(fetchPublishAllCount({ boardId: action.payload.boardId }));
      if (results.originalBlock?.objectId) {
        yield put(getWorklistDataByIdRequest({
          ids: [results.originalBlock?.objectId],
          worklistId,
          highlightIds: [
            results.originalBlock?.objectId,
            results.originalBlock?.id,
            results.splittedBlock.id,
          ],
        }));
      }
      yield put(splitBlockSuccess(results));
    } else {
      yield put(updateDataFromSockets())
    }
  } catch (err) {
    yield put(splitBlockFailure(err));
  }
}

export function* doSetView(action) {
  const boardData = yield select(getBoardData);
  const id = action.payload;

  const { views } = boardData;
  const period = views?.find(view => view?.id === id);
  if (period) {
    yield put(setViewInternal(period));
  }
}

export function* doFetchPublishAllCount(action) {
  try {
    const { boardId = 'service' } = action.payload;
    const results = yield call(callApi, {
      endpoint:
        // eslint-disable-next-line
        `planning/${boardId}/publishCount`,
      options: {
        errorMessage: 'Could not get publish all count',
        retryAction: action,
      },
    });

    yield put(fetchPublishAllCountSuccess(results));
  } catch (err) {
    yield put(fetchPublishAllCountFailure(err));
  }
}

export function* getGanttViews(action) {
  try {
    const { id } = action.payload;
    const result = yield call(callApi, {
      endpoint: `/worklist/${id}/template`,
      options: {
        errorMessage: `Could not get gantt views: ${action.payload.id}`,
        retryAction: action,
      },
    });
    yield put(getGanttViewsSuccess(result));
  } catch (err) {
    yield put(getGanttViewsFailure(err));
  }
}

export function* createGanttView(action) {
  try {
    const allFSEs = yield select(getAllFSEs);
    const {
      id,
      templateName,
      filterData,
      sorting = [],
      isDefault,
      viewRange,
    } = action.payload;
    const request = {
      templateName,
      filterData,
      sorting: sorting?.map(s => ({
        ...s,
        manualSort: s.sortDirection === sortTypes.manual ? allFSEs : [],
      })),
      isDefault,
      viewRange,
    };
    const result = yield call(callApi, {
      endpoint: `/worklist/${id}/template/new`,
      options: {
        method: 'POST',
        body: JSON.stringify(request),
        errorMessage: `Could not save gantt view: ${action.payload.id}`,
        retryAction: action,
      },
    });
    yield put(createGanttViewSuccess(result));
  } catch (err) {
    yield put(createGanttViewFailure(err));
  }
}

export function* updateGanttViews(action) {
  try {
    const { id, views } = action.payload;

    const result = yield call(callApi, {
      endpoint: `/worklist/${id}/template`,
      options: {
        method: 'PUT',
        body: JSON.stringify(views),
        errorMessage: `Could not update gantt views: ${action.payload.id}`,
        retryAction: action,
      },
    });
    yield put(updateGanttViewsSuccess(result));
  } catch (err) {
    yield put(updateGanttViewsFailure(err));
  }
}

// eslint-disable-next-line complexity
export function* openPlanningAndView(action) {
  try {
    const { id, history, filters } = action.payload;

    const result = yield call(callApi, {
      endpoint: `/planning/${action.payload.boardId}/${id}/jump`,
      options: {
        method: 'GET',
        errorMessage: `Could not jump to ${id}`,
        retryAction: action,
      },
    });
    yield put(openPlanningAndViewSuccess({
      ...action.payload,
      ...result,
    }));
    const defaultSorting = yield select(getDefaultSort);
    // eslint-disable-next-line
    history.push(`/planning-board/serviceplanning${filters ? `/filters/${action.payload.filters}` : ""}${defaultSorting ? `/sort/${defaultSorting}` : ''}/view/serviceplanning_none`);
  } catch (err) {
    const reloadAction = err.error?.actions?.find(a => a.id === 'reload');
    if (err.error?.status === 410 && reloadAction) {
      yield put(openPlanningAndViewAction({
        ...action.payload,
        id: reloadAction.toId,
      }));
    } else {
      yield put(() => {
        notification.error({
          message: action.payload.intl?.formatMessage({ id: 'planningBoard.REDIRECT_TITLE' }),
          description: err?.error?.message ||
            action.payload.intl?.formatMessage({ id: 'planningBoard.REDIRECT_DESCRIPTION' }),
          className: 'extended-notification',
          key: 'redirect_error',
          duration: 0,
          placement: 'top',
        });
      });
      yield put(openPlanningAndViewFailure(err));
    }
  }
}

export function* doDraggingOver() {
  // get lanes info
  const lanesInfo = yield select(getLanesInfo);
  const isDraggingOver = !!lanesInfo?.find(info => info.isDraggingOver);
  yield put(setIsDraggingOver(isDraggingOver));
}

export function* fetchRecommendedEngineers(action) {
  try {
    const { plannerGroup } = action.payload;

    const result = yield call(callApi, {
      endpoint: `/planning/forward/recommendedengineers/${plannerGroup}`,
      options: {
        method: 'GET',
        errorMessage: 'Could not fetch available recommended engineer',
        retryAction: action,
      },
    });
    if (action.payload.callback) {
      action.payload.callback(result);
    }
    yield put(fetchRecommendedEngineersSuccess(result));
  } catch (err) {
    if (action.payload.callback) {
      action.payload.callback(err);
    }
    yield put(fetchRecommendedEngineersFailure(err));
  }
}

export function* calculateRouteOptimisation(action) {
  try {
    const { to, from, technicianIds } = action.payload;

    const result = yield call(callApi, {
      endpoint: '/planning/forward',
      options: {
        method: 'POST',
        body: JSON.stringify({ from, to, technicianIds }),
        errorMessage: 'Could not calculate Route Optimisation',
        retryAction: action,
      },
    });
    if (action.payload.callback) {
      action.payload.callback(result);
    }
    yield put(calculateRouteOptimisationSuccess(action.payload));
  } catch (err) {
    yield put(calculateRouteOptimisationFailure(err));
  }
}

export function* abortRouteOptimisation(action) {
  try {
    const { forwardPlanningId } = action.payload;

    yield call(callApi, {
      endpoint: `planning/forward/${forwardPlanningId}/abort`,
      options: {
        method: 'PUT',
        errorMessage: 'Abort Route Optimisation failed',
        retryAction: action,
      },
    });
    yield put(abortRouteOptimisationSuccess(action.payload));
  } catch (err) {
    yield put(abortRouteOptimisationFailure(err));
  }
}

export function* retryRouteOptimisation(action) {
  try {
    const { forwardPlanningId } = action.payload;

    yield call(callApi, {
      endpoint: `planning/forward/${forwardPlanningId}/retry`,
      options: {
        method: 'PUT',
        errorMessage: 'Retry Route Optimisation failed',
        retryAction: action,
      },
    });
    yield put(fetchForwardBlockData({ boardId: 'forward', id: forwardPlanningId }));
    yield put(retryRouteOptimisationSuccess(action.payload));
  } catch (err) {
    if (action.payload.callback) {
      action.payload.callback(err);
    }
    yield put(retryRouteOptimisationFailure(err));
  }
}

export const getLaneData = state => state.gantt.lanesData;
export const getView = state => state.gantt.view;
export const getUserId = state => state.user.id;
export function* doFetchPlanningDataPostSocketUpdate(action) {
  const lanesData = yield select(getLaneData);
  const dateReference = yield select(getDateReference);
  const view = yield select(getView);
  const userId = yield select(getUserId);
  const { start, technicianId, entityId, triggeredById, orderId } = action.payload;
  const startDate = moment(start);
  const dateString = getDateToString(dateReference, view);
  const { startHour, endHour, workingDays, numberOfWeeks } = view.parameters;
  const viewPortStart =
    moment(dateString, 'YYYYMMDD').set({ hour: startHour, minute: 0, second: 0 });
  // eslint-disable-next-line no-nested-ternary
  const amountOfViewPortDays = view?.id === viewIds.twoWorkWeeks ?
    12 :
    view?.id === viewIds.month ?
      31 :
      workingDays.length * numberOfWeeks;
  const viewPortEnd =
    viewPortStart.add(amountOfViewPortDays - 1, 'day')
      .set({ hour: endHour, minute: 0, second: 0 });

  const inViewPort =
    startDate.isSameOrAfter(viewPortStart) && startDate.isSameOrBefore(viewPortEnd);
  try {
    if (inViewPort) {
      const lane = lanesData.find(l => l.id === technicianId);
      const oldLane = lanesData.find(l =>
        l.blocks.find(b => b.id === entityId) &&
        l.id !== technicianId,
      );
      if (lane) {
        const block = lane.blocks.find(b => b.id === entityId);
        const isUpdate = !!block || !!oldLane;
        const results = yield call(callApi, {
          endpoint:
            `planning/service/${entityId}`,
          options: {
            errorMessage: 'Could not fetch block data',
            retryAction: action,
          },
        });
        yield put(fetchPublishAllCount({ boardId: action.payload.boardId }));

        if (isUpdate) {
          if (!oldLane) {
            yield put(updateBlockDataSuccess({
              additionalBlocks: [],
              block: results,
              isSidePanel: false,
              externalUpdate: triggeredById && triggeredById !== userId,
            }));
          } else {
            yield put(updateBlockOtherLaneDataSuccess({
              additionalBlocks: [],
              block: results,
              oldLaneId: oldLane.id,
              isSidePanel: false,
              addBlockFromSocketUpdate: true,
              externalUpdate: triggeredById && triggeredById !== userId,
            }));
          }
        } else {
          yield put(addBlockDataSuccess({
            additionalBlocks: [],
            block: results,
            isSidePanel: action.payload.isSidePanel,
            addBlockFromSocketUpdate: true,
            externalUpdate: triggeredById && triggeredById !== userId,
          }));
        }
        if (orderId) {
          yield put(getWorklistDataByIdRequest({
            ids: [orderId],
            worklistId: planningBoardWorklistId,
            highlightIds: [orderId, entityId],
          }));
        }
        yield put(fetchPlanningDataPostSocketUpdateSuccess(results));
      }
    }
  } catch (err) {
    yield put(fetchPlanningDataPostSocketUpdateFailure(err));
  }
}

export function* doDeletePlanningDataPostSocket(action) {
  const userId = yield select(getUserId);
  const { technicianId, entityId, triggeredById, orderId } = action.payload;

  yield put(deletePlanningSuccess({
    isSidepanel: false,
    externalUpdate: triggeredById && triggeredById !== userId,
    block: {
      id: entityId,
      laneId: technicianId,
    },
    additionalBlocks: [],
  }));
  if (orderId) {
    yield put(getWorklistDataByIdRequest({
      ids: [orderId],
      worklistId: planningBoardWorklistId,
      highlightIds: [orderId, entityId],
    }));
  }
}

export function* doFetchForwardBlocksData(action) {
  const { ids } = action.payload;

  if (ids?.length) {
    for (const id of ids) {
      yield put(fetchForwardBlockData({
        boardId: 'forward',
        id,
      }));
    }
  }
}

function* watchFetchBoardMetadata() {
  yield takeLatest(actionTypes.FETCH_BOARD_METADATA, doFetchBoardMetadate);
}

function* watchFetchBoardData() {
  yield takeLatest(actionTypes.FETCH_BOARD_DATA, doFetchBoardData);
}

function* watchFetchViewData() {
  yield takeLatest(actionTypes.FETCH_VIEW_DATA, doFetchViewData);
}

function* watchUpdateBlockData() {
  yield takeEvery(actionTypes.UPDATE_BLOCK_DATA, doUpdateBlockData);
}

function* watchFetchBlockData() {
  yield takeEvery(actionTypes.FETCH_BLOCK_DATA, doFetchBlockData);
}

function* watchFetchForwardBlockData() {
  yield takeEvery(actionTypes.FETCH_FORWARD_BLOCK_DATA, doFetchForwardBlockData);
}

function* watchFetchForwardBlocksData() {
  yield takeEvery(actionTypes.FETCH_FORWARD_BLOCKS_DATA, doFetchForwardBlocksData);
}

function* watchPublishToSAP() {
  yield takeEvery(actionTypes.PUBLISH_TO_SAP, doPublishToSAP);
  yield takeEvery(PUBLISH_TO_SAP_QUICK_ACTION, doPublishToSAP);
}

function* watchPublishAllToSAP() {
  yield takeLatest(actionTypes.PUBLISH_ALL_TO_SAP, doPublishAllToSAP);
}

function* watchMarkDateConfirmed() {
  yield takeEvery(actionTypes.MARK_DATE_CONFIRMED, doMarkDateConfirmed);
}

function* watchMarkDateUnconfirmed() {
  yield takeEvery(actionTypes.MARK_DATE_UNCONFIRMED, doMarkDateUnconfirmed);
}

function* watchRestoreBackup() {
  yield takeEvery(actionTypes.RESTORE_BACKUP, doRestoreBackup);
}

function* watchRecalculateTravelTime() {
  yield takeEvery(actionTypes.RECALCULATE_TRAVEL_TIME, doRecalculateTravelTime);
}

function* watchAddBlockData() {
  yield takeEvery(actionTypes.ADD_BLOCK_DATA, doAddBlockData);
}

function* watchAddMultiBlockData() {
  yield takeEvery(actionTypes.ADD_MULTI_BLOCK_DATA, doAddMultiBlockData);
}

function* watchUpdateMultiPlanningBlockData() {
  yield takeEvery(actionTypes.UPDATE_MULTI_PLANNING_BLOCK_DATA, doUpdateMultiPlanningBlockData);
}

function* watchDeleteBlock() {
  yield takeEvery(actionTypes.DELETE_BLOCK, doDeleteBlock);
}

function* watchSplitBlock() {
  yield takeEvery(actionTypes.SPLIT_BLOCK, doSplitBlock);
}

function* watchDraggingOver() {
  yield takeLatest(actionTypes.SET_LANE_INFO_IS_DRAGGING_OVER, doDraggingOver);
}

function* watchSetView() {
  yield takeLatest(actionTypes.SET_VIEW, doSetView);
}

function* watchFetchPublishAllCount() {
  yield takeLatest(actionTypes.FETCH_PUBLISH_ALL_COUNT, doFetchPublishAllCount);
}

function* watchFetchGanttViews() {
  yield takeLatest(actionTypes.GET_GANTT_VIEWS_REQUEST, getGanttViews);
}

function* watchCreateGanttViews() {
  yield takeLatest(actionTypes.CREATE_GANTT_VIEW_REQUEST, createGanttView);
}

function* watchUpdateGanttViews() {
  yield takeLatest(actionTypes.UPDATE_GANTT_VIEWS_REQUEST, updateGanttViews);
}

function* watchOpenPlanningAndView() {
  yield takeLatest(actionTypes.OPEN_PLANNING_AND_VIEW, openPlanningAndView);
}

function* watchFetchRecommendedEngineers() {
  yield takeLatest(actionTypes.FETCH_RECOMMENDED_ENGINEERS,
    fetchRecommendedEngineers);
}

function* watchCalculateRouteOptimisation() {
  yield takeLatest(actionTypes.CALCULATE_ROUTE_OPTIMISATION,
    calculateRouteOptimisation);
}

function* watchAbortRouteOptimisation() {
  yield takeLatest(actionTypes.ABORT_ROUTE_OPTIMISATION,
    abortRouteOptimisation);
}

function* watchRetryRouteOptimisation() {
  yield takeLatest(actionTypes.RETRY_ROUTE_OPTIMISATION,
    retryRouteOptimisation);
}

function* watchFetchPlanningDataPostSocketUpdate() {
  yield takeEvery(actionTypes.FETCH_PLANNING_DATA_POST_SOCKET_UPDATE,
    doFetchPlanningDataPostSocketUpdate);
}

function* watchDeletePlanningDataPostSocket() {
  yield takeEvery(actionTypes.DELETE_PLANNING_DATA_POST_SOCKET,
    doDeletePlanningDataPostSocket);
}

export default [
  fork(watchFetchBoardData),
  fork(watchFetchViewData),
  fork(watchUpdateBlockData),
  fork(watchFetchBlockData),
  fork(watchFetchForwardBlockData),
  fork(watchPublishToSAP),
  fork(watchPublishAllToSAP),
  fork(watchMarkDateConfirmed),
  fork(watchMarkDateUnconfirmed),
  fork(watchRestoreBackup),
  fork(watchAddBlockData),
  fork(watchAddMultiBlockData),
  fork(watchUpdateMultiPlanningBlockData),
  fork(watchDraggingOver),
  fork(watchDeleteBlock),
  fork(watchSplitBlock),
  fork(watchSetView),
  fork(watchFetchPublishAllCount),
  fork(watchFetchGanttViews),
  fork(watchCreateGanttViews),
  fork(watchUpdateGanttViews),
  fork(watchOpenPlanningAndView),
  fork(watchFetchBoardMetadata),
  fork(watchRecalculateTravelTime),
  fork(watchFetchRecommendedEngineers),
  fork(watchCalculateRouteOptimisation),
  fork(watchAbortRouteOptimisation),
  fork(watchRetryRouteOptimisation),
  fork(watchFetchPlanningDataPostSocketUpdate),
  fork(watchDeletePlanningDataPostSocket),
  fork(watchFetchForwardBlocksData),
];
